/* ============================================================
   Everything after the chapter: reveal, parallax, the manifesto's
   word-by-word lighting, and the resample smoothing that keeps a
   small source soft instead of blocky. (§5 AFTER THE CHAPTER)
   ============================================================ */

export function initPanels(): () => void {
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (REDUCED) {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
    return () => {};
  }

  const io = new IntersectionObserver(
    (es) =>
      es.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }),
    { rootMargin: '0px 0px -12% 0px' },
  );
  document.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => {
    const parent = el.parentElement;
    if (parent) {
      const sibs = Array.from(parent.children).filter((c) => c.hasAttribute('data-reveal'));
      el.style.setProperty('--i', String(sibs.indexOf(el)));
    }
    io.observe(el);
  });

  const layers = Array.from(document.querySelectorAll<HTMLElement>('[data-parallax]'));
  let raf = 0;
  const paint = () => {
    raf = 0;
    const h = innerHeight;
    for (const el of layers) {
      const r = el.getBoundingClientRect();
      if (r.bottom < -h || r.top > 2 * h) continue;
      const centre = (r.top + r.height / 2 - h / 2) / h;
      let speed = parseFloat(el.dataset.parallax || '0.32');
      if (el.classList.contains('panel-bg') && innerWidth < 780) speed = Math.min(speed, 0.22);
      el.style.setProperty('--py', (centre * -speed * h).toFixed(1) + 'px');
    }
  };

  const soften = (el: HTMLElement) => {
    const media = el as HTMLImageElement & HTMLVideoElement;
    const nw = media.naturalWidth || media.videoWidth;
    const nh = media.naturalHeight || media.videoHeight;
    if (!nw || !nh) return;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const factor = Math.max(r.width / nw, r.height / nh);
    const px = factor <= 1.15 ? 0 : Math.min(1.6, Math.max(0.4, (factor - 1) * 1.2));
    el.style.setProperty('--soften', px.toFixed(2) + 'px');
  };
  const SOFT = '.stage video, .plate, .panel-bg, .closing-bg';
  const softenAll = () =>
    document.querySelectorAll<HTMLElement>(SOFT).forEach((el) => soften(el));
  const onLoad = (e: Event) => {
    const t = e.target as Element | null;
    if (t && t.matches && t.matches(SOFT)) soften(t as HTMLElement);
  };
  document.addEventListener('load', onLoad, true);
  document.addEventListener('loadedmetadata', onLoad, true);
  addEventListener('resize', softenAll);
  softenAll();

  // Manifesto — wrap words once, then light them in order from section progress.
  const manifestos = Array.from(document.querySelectorAll<HTMLElement>('[data-words]')).map((el) => {
    if (el.dataset.wordsDone === '1') {
      return { section: (el.closest('section') || el) as HTMLElement, words: Array.from(el.querySelectorAll<HTMLElement>('.w')) };
    }
    el.dataset.wordsDone = '1';
    const wrap = (node: Node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const text = child.textContent || '';
          const frag = document.createDocumentFragment();
          text.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const w = document.createElement('span');
            w.className = 'w';
            w.textContent = part;
            frag.appendChild(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1) {
          wrap(child);
        }
      });
    };
    wrap(el);
    return { section: (el.closest('section') || el) as HTMLElement, words: Array.from(el.querySelectorAll<HTMLElement>('.w')) };
  });

  const light = () => {
    const h = innerHeight;
    for (const m of manifestos) {
      const r = m.section.getBoundingClientRect();
      if (r.bottom < 0 || r.top > h) continue;
      const p = Math.min(1, Math.max(0, (h * 0.8 - r.top) / (r.height + h * 0.45)));
      const n = m.words.length;
      m.words.forEach((w, i) => w.style.setProperty('--lit', p * (n + 1) > i + 1 ? '1' : '0'));
    }
  };

  const onScroll = () => {
    if (!raf) raf = requestAnimationFrame(paint);
    requestAnimationFrame(light);
  };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', paint);
  paint();
  light();

  return () => {
    io.disconnect();
    document.removeEventListener('load', onLoad, true);
    document.removeEventListener('loadedmetadata', onLoad, true);
    removeEventListener('resize', softenAll);
    removeEventListener('scroll', onScroll);
    removeEventListener('resize', paint);
  };
}